import { Toaster } from 'react-hot-toast';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useAppStartHooks } from '../hooks/main_page/useAppStartHooks';
import { persistor, store } from '../redux';
import '../redux/epics/index.listener';
import { BrowserRouter } from '../routing/BrowserRouter';
import { history } from '../routing/history';
import Routes from '../routing/Routes';
import Footer from './main/Footer';
import { NavBar } from './main/Navbar';
import BottomNav from './main/BottomNav';
import CompareFab from './common/buttons/CompareFab';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import PrivacyCoockies from './main_page/sections/privacy_policy/PrivacyCoockies';
import GeLangBanner from './main/GeLangBanner';
import { PostCreateUpsellModal } from './common/modals/PostCreateUpsellModal';
import { WhatsNewToast } from './common/WhatsNewToast';
import { ErrorBoundary } from './common/ErrorBoundary';
import PageError from './main_page/main/PageError';
import { useGoogleAnalytics } from '../hooks/google/useGoogleAnalytics';
import {
    setupCacheCleanup,
    stopCacheCleanup,
} from '../redux/reducers/cache/cacheCleanup';
import { HTML_LANG_MAP, isSupportedLang } from '../constants/languages';

const SEO_FALLBACK_VISIBLE_RE =
    /^\/(?:ge|ru|en|ua)\/(?:(?:search|rent)\/(?!cabinet\/)|sell\/(?:car|bike|commercial)$|$)/;

const SEO_FALLBACK_HOME_RE = /^\/(?:ge|ru|en|ua)\/$/;

const SeoFallbackHandler = () => {
    const location = useLocation();
    const initialPath = useRef(location.pathname);

    useEffect(() => {
        const fallback = document.getElementById('seo-fallback-content');
        if (!fallback) return undefined;

        if (
            location.pathname === initialPath.current &&
            SEO_FALLBACK_VISIBLE_RE.test(location.pathname)
        ) {
            const slot = document.getElementById('seo-fallback-slot');
            if (slot && fallback.parentNode !== slot) {
                slot.appendChild(fallback);
            }
            if (SEO_FALLBACK_HOME_RE.test(location.pathname)) {
                fallback
                    .querySelectorAll('.seo-faq-heading, .seo-faq-item')
                    .forEach((node) => {
                        node.hidden = true;
                    });
            }
            fallback.hidden = false;
            return undefined;
        }

        const hideFallback = () => {
            fallback.hidden = true;
        };

        if (document.readyState === 'complete') {
            hideFallback();
            return undefined;
        }

        window.addEventListener('load', hideFallback, { once: true });
        return () => window.removeEventListener('load', hideFallback);
    }, [location.pathname]);

    return null;
};

const LanguageHandler = ({ children }) => {
    const { i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    useGoogleAnalytics();

    useEffect(() => {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        const langFromUrl = pathSegments[0];

        const hasDoubleLang =
            pathSegments.length > 1 && isSupportedLang(pathSegments[1]);

        if (hasDoubleLang) {
            const correctPath =
                `/${pathSegments.filter((_, i) => i !== 1).join('/')}`;
            navigate(correctPath + location.search, { replace: true });
            return;
        }

        if (isSupportedLang(langFromUrl)) {
            if (i18n.language !== langFromUrl) {
                i18n.changeLanguage(langFromUrl);
            }

            if (pathSegments.length === 1 && !location.pathname.endsWith('/')) {
                navigate(`/${langFromUrl}/${location.search}`, {
                    replace: true,
                });
            }
        } else if (
            location.pathname === '/' ||
            !isSupportedLang(langFromUrl)
        ) {
            const currentLang = i18n.language || 'ge';
            const newPath =
                location.pathname === '/'
                    ? `/${currentLang}/`
                    : `/${currentLang}${location.pathname}`;
            navigate(newPath + location.search, { replace: true });
        }
    }, [location.pathname, location.search, i18n, navigate]);

    return (
        <>
            <Helmet>
                <html lang={HTML_LANG_MAP[i18n.language] || HTML_LANG_MAP.ge} />
            </Helmet>
            {children}
        </>
    );
};

const RoutesWithErrorBoundary = () => {
    const { pathname } = useLocation();

    return (
        <ErrorBoundary fallback={<PageError />} resetKey={pathname}>
            <Routes />
        </ErrorBoundary>
    );
};

export default function App() {
    useAppStartHooks(store);

    useEffect(() => {
        setupCacheCleanup(store);
        return () => {
            stopCacheCleanup();
        };
    }, []);

    return (
        <HelmetProvider>
            <Provider store={store}>
                <PersistGate loading={null} persistor={persistor}>
                    <BrowserRouter history={history}>
                        <SeoFallbackHandler />
                        <LanguageHandler>
                            <div className="app">
                                <NavBar />
                                <main className="app__main">
                                    <RoutesWithErrorBoundary />
                                </main>
                                <div id="seo-fallback-slot" />
                                <Footer />
                                <BottomNav />
                                <CompareFab />
                                <Toaster />
                            </div>
                        </LanguageHandler>
                        <PrivacyCoockies />
                        <GeLangBanner />
                        <PostCreateUpsellModal />
                        <WhatsNewToast />
                    </BrowserRouter>
                </PersistGate>
            </Provider>
        </HelmetProvider>
    );
}
